// GL6 (#6) — personel satırının aktivasyon bağlantısı eylemleri (gönder / tekrar gönder / iptal), EP + SP ortak.
// Her uygulama yalnız farklı olanı verir: etkinleştirme istemcisi, satır dönüş hedefi, ⋮ tetikleyicileri ve
// kişisel verinin maskelenip maskelenmediği. Kanca aynı zamanda satır-kaldırma odak durumunu (`useRowRemovalFocus`)
// taşır: iptal onayı ve bağlantı penceresi onun `dialogOpen` / `otherSurfaceOpen` girdileridir.
//
// Davranış (değişmez): iptal hedefi açılışta DURUM olarak yakalanır (tazeleme satırı kaldırsa da hata ve ad
// pencerede kalır); bağlantı penceresinin hedefi YALNIZ pencerenin `onClose`'undan boşaltılır (tek bağlantı
// kopyası tazeleme ile yitmesin); iptal onayı yalnız tutulan bir hedef varken açıktır.

import { useMutation } from '@tanstack/react-query';
import type { RefObject } from 'react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ActivationLinkResult, ContractorPersonnelDto } from '../api/contractors';
import type { RowAction } from '../ui/RowActionsMenu';
import { type RowRemovalFocus, useRowRemovalFocus } from '../ui/useRowRemovalFocus';
import type { PersonnelLinkModalProps, PersonnelLinkTarget } from './PersonnelLinkModal';
import type { PersonnelActivationClient } from './activationClient';
import { isStaleLinkError } from './activationLinkErrors';
import { personLabel } from './personLabel';
import { invitationUuidOf, isNotActivated } from './personnelStatus';

/** İptal onayının açılışta yakalanan hedefi (tazeleme satırı kaldırsa da pencere bunu gösterir). */
interface CancelTarget {
  rowUuid: string;
  invitationUuid: string;
  kind: ContractorPersonnelDto['kind'];
  name: string;
}

export interface UsePersonnelLinkActionsOptions {
  activation: PersonnelActivationClient;
  /** Satır kimliklerinin TAM listesi (`useRowRemovalFocus.rowKeys`). */
  rowKeys: readonly string[];
  stamps: { dataUpdatedAt: number; errorUpdatedAt: number };
  /** Kalıcı "Personel oluştur" düğmesi: odak zincirinin son halkası. */
  addButtonRef: RefObject<HTMLElement | null>;
  /** Çağıranın kendi onay diyaloğu (silme / çıkarma) açık mı. */
  dialogOpen: boolean;
  /** Çağıranın kendi çekmece/sheet'i açık mı (bağlantı penceresi buraya eklenir). */
  otherSurfaceOpen: boolean;
  /** Satırdan açılan pencerenin dönüş hedefi: ad düğmesi → ⋮ → oluştur düğmesi. */
  rowOpener: (person: ContractorPersonnelDto) => HTMLElement | null;
  /** Satırların ⋮ tetikleyicileri (anahtar = satır uuid). Düz davet tekrar gönderiminden sonra yeni satırı bulmak için. */
  triggers: RefObject<ReadonlyMap<string, HTMLElement>>;
  /** Portal kişisel veriyi maskeleyebiliyor mu (EP evet; SP hiç maskelemez). */
  honorPersonalDataMask: boolean;
}

export interface PersonnelLinkActions {
  focus: RowRemovalFocus;
  /** Satırı kaldıran/kalan eylemlerden önce, diyalog açılırken çağrılır. */
  rememberRow: (person: ContractorPersonnelDto) => void;
  /** ⋮ menüsüne eklenecek bağlantı öğeleri (boş = bu satırda bağlantı eylemi yok). */
  linkItems: (person: ContractorPersonnelDto) => RowAction[];
  /** `PersonnelLinkModal`'a açılır. */
  linkModalProps: Pick<
    PersonnelLinkModalProps,
    'client' | 'target' | 'onClose' | 'restoreFocusRef'
  >;
  cancelDialogProps: {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    title: string;
    body: string;
    confirmLabel: string;
    restoreFocusRef: RefObject<HTMLElement | null>;
    loading: boolean;
    error: unknown;
    errorMessage: string | undefined;
    /** Bayat bağlantı iptali başarısız oldu: liste tazelendi, tekrar denemek aynı 404'ü verir. */
    confirmDisabled: boolean;
    confirmVariant: 'primary' | 'danger';
    onConfirm: () => void;
  };
}

export function usePersonnelLinkActions({
  activation,
  rowKeys,
  stamps,
  addButtonRef,
  dialogOpen,
  otherSurfaceOpen,
  rowOpener,
  triggers,
  honorPersonalDataMask,
}: UsePersonnelLinkActionsOptions): PersonnelLinkActions {
  const { t } = useTranslation();
  // Bağlantı gönder / tekrar gönder penceresi ve ⋮ dönüş hedefi. Hedef açılışta yakalanır, YALNIZ
  // `onClose`'dan temizlenir (listeden türetilmez: yenileme başarı adımını söküp tek bağlantıyı yitirirdi).
  const [linkTarget, setLinkTarget] = useState<PersonnelLinkTarget | null>(null);
  const linkRestoreRef = useRef<HTMLElement | null>(null);
  // Açılan satırın türü: düz davetin tekrar gönderimi satırı YENİDEN anahtarlar (yeni uuid → yeni satır).
  const linkRowKindRef = useRef<ContractorPersonnelDto['kind'] | null>(null);
  const [toCancel, setToCancel] = useState<CancelTarget | null>(null);

  const focus = useRowRemovalFocus({
    rowKeys,
    stamps,
    addButtonRef,
    dialogOpen: dialogOpen || toCancel !== null,
    otherSurfaceOpen: otherSurfaceOpen || linkTarget !== null,
  });

  // Düz davet iptalinde satır listeden KALKAR (§4.6) → `focus.removed`; kişinin bağlantısı yerinde
  // temizlenir, satır kalır. 404 INVITATION_NOT_FOUND (başka yerde iptal/aktive): hata + tazele.
  const cancelMutation = useMutation({
    mutationFn: (vars: CancelTarget) => activation.cancel(vars.invitationUuid),
    onSuccess: (_data, vars) => {
      activation.invalidate();
      if (vars.kind === 'Invitation') {
        focus.removed(vars.rowUuid);
      }
      setToCancel(null);
    },
    onError: (_error, vars) => {
      activation.invalidate();
      focus.removeFailed(vars.rowUuid);
    },
  });

  function rememberRow(person: ContractorPersonnelDto) {
    focus.rememberOpener(rowOpener(person));
  }
  function openLink(person: ContractorPersonnelDto, target: PersonnelLinkTarget) {
    // Aynı zincir (ad → ⋮ → oluştur): menü öğesi kapanınca DOM'dan kalkar, ⋮ yalnız ikinci seçenektir.
    linkRestoreRef.current = rowOpener(person);
    linkRowKindRef.current = person.kind;
    setLinkTarget(target);
  }
  function closeLink(result: ActivationLinkResult | null) {
    // Düz davet tekrar gönderildi: eski satır gitti, odak YENİ satırın ⋮'sine (henüz yoksa oluştur düğmesine) döner.
    if (result && linkRowKindRef.current === 'Invitation') {
      linkRestoreRef.current = triggers.current?.get(result.invitationUuid) ?? addButtonRef.current;
    }
    setLinkTarget(null);
  }
  function askCancel(person: ContractorPersonnelDto) {
    const invitationUuid = invitationUuidOf(person);
    if (invitationUuid === null) {
      return;
    }
    cancelMutation.reset();
    rememberRow(person);
    setToCancel({
      rowUuid: person.uuid,
      invitationUuid,
      kind: person.kind,
      name: personLabel(person),
    });
  }
  function linkItems(person: ContractorPersonnelDto): RowAction[] {
    const name = personLabel(person);
    if (person.kind === 'Invitation') {
      return [
        {
          key: 'resend',
          label: t('personnelLink.action.resend'),
          onSelect: () =>
            openLink(person, {
              mode: 'resend',
              invitationUuid: person.uuid,
              personName: name,
              availableChannels: person.availableChannels,
            }),
        },
        {
          key: 'cancel',
          label: t('personnelLink.action.cancel'),
          destructive: true,
          onSelect: () => askCancel(person),
        },
      ];
    }
    if (!isNotActivated(person)) {
      return []; // aktif / pasif kişi: bağlantı eylemi yok (isActive !== true dahil)
    }
    const link = person.pendingLink;
    return link
      ? [
          {
            key: 'resend',
            label: t('personnelLink.action.resend'),
            onSelect: () =>
              openLink(person, {
                mode: 'resend',
                invitationUuid: link.invitationUuid,
                personName: name,
                availableChannels: link.availableChannels,
              }),
          },
          {
            key: 'cancel',
            label: t('personnelLink.action.cancel'),
            onSelect: () => askCancel(person),
          },
        ]
      : [
          {
            key: 'send',
            label: t('personnelLink.action.send'),
            onSelect: () =>
              openLink(person, {
                mode: 'send',
                userUuid: person.uuid,
                personName: name,
                // Maskeleme null'ı null bırakır; "T.C. var" için maskeli değer de yeter.
                tcMissing: person.tcKimlikNo === null,
                personalDataMasked: honorPersonalDataMask && person.personalDataMasked === true,
              }),
          },
        ];
  }

  return {
    focus,
    rememberRow,
    linkItems,
    linkModalProps: {
      client: activation,
      target: linkTarget,
      onClose: closeLink,
      restoreFocusRef: linkRestoreRef,
    },
    cancelDialogProps: {
      open: toCancel !== null,
      onOpenChange: (open) => {
        if (!open) {
          setToCancel(null);
        }
      },
      title: t('personnelLink.cancel.title'),
      // Düz davet satırı onaydan sonra listeden KALKAR; kişi satırı "Etkinleştirilmedi" olarak kalır.
      body: t(
        toCancel?.kind === 'Invitation'
          ? 'personnelLink.cancel.body.invite'
          : 'personnelLink.cancel.body.person',
        { name: toCancel?.name ?? '' },
      ),
      confirmLabel: t('personnelLink.cancel.confirm'),
      // Kişi satırında bağlantı iptali geri alınabilir (yeniden gönderilir) → nötr; düz davet satırı kalkar.
      confirmVariant: toCancel?.kind === 'Invitation' ? ('danger' as const) : ('primary' as const),
      restoreFocusRef: focus.menuRestoreRef,
      loading: cancelMutation.isPending,
      error: cancelMutation.isError ? cancelMutation.error : undefined,
      // Bayat bağlantı: backend metni "davet bulunamadı" der (yanlış isim, çaresiz) → nötr, firma-yüzü metin.
      errorMessage: isStaleLinkError(cancelMutation.error)
        ? t('personnelLink.notFound')
        : undefined,
      confirmDisabled: isStaleLinkError(cancelMutation.error),
      onConfirm: () => {
        if (toCancel) {
          cancelMutation.mutate(toCancel);
        }
      },
    },
  };
}
